export interface RequestSnapshot {
  jobTitle?: string | null;
  declaredStartDate?: string | null;
  employerAddressNormalized?: string | null;
  employerPhoneNormalized?: string | null;
  employerPhoneVerified?: boolean | null;
}

export type SnapshotFieldKey =
  | 'jobTitle'
  | 'declaredStartDate'
  | 'employerAddressNormalized'
  | 'employerPhoneNormalized'
  | 'employerPhoneVerified';

export type MutationSeverity = 'none' | 'advisory' | 'blocking';

export interface SnapshotDiffLine {
  field: SnapshotFieldKey;
  label: string;
  previous: string | null;
  current: string | null;
  mutationSeverity: MutationSeverity;
}

export const SNAPSHOT_FIELD_LABELS: Record<SnapshotFieldKey, string> = {
  jobTitle: 'Role',
  declaredStartDate: 'Start date',
  employerAddressNormalized: 'Employer address',
  employerPhoneNormalized: 'Employer phone',
  employerPhoneVerified: 'Employer phone verified',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function readSnapshot(value: unknown): RequestSnapshot | null {
  const row = asRecord(value);
  if (!row) return null;

  return {
    jobTitle: readString(row.jobTitle),
    declaredStartDate: readString(row.declaredStartDate),
    employerAddressNormalized: readString(row.employerAddressNormalized),
    employerPhoneNormalized: readString(row.employerPhoneNormalized),
    employerPhoneVerified:
      typeof row.employerPhoneVerified === 'boolean' ? row.employerPhoneVerified : null,
  };
}

export function readRequestSnapshotFromEvidence(evidence: unknown): RequestSnapshot | null {
  const evidenceRecord = asRecord(evidence);
  if (!evidenceRecord) return null;
  return readSnapshot(evidenceRecord.requestSnapshot);
}

function formatSnapshotValue(field: SnapshotFieldKey, value: unknown): string | null {
  if (value == null || value === '') return null;
  if (field === 'employerPhoneVerified') {
    return value === true ? 'Yes' : value === false ? 'No' : null;
  }
  return String(value);
}

export function diffRequestSnapshots(
  previous: RequestSnapshot | null,
  current: RequestSnapshot | null,
): SnapshotDiffLine[] {
  if (!previous || !current) return [];

  const fields = Object.keys(SNAPSHOT_FIELD_LABELS) as SnapshotFieldKey[];
  const lines: SnapshotDiffLine[] = [];

  for (const field of fields) {
    const prev = formatSnapshotValue(field, previous[field]);
    const curr = formatSnapshotValue(field, current[field]);
    if (prev === curr) continue;

    lines.push({
      field,
      label: SNAPSHOT_FIELD_LABELS[field],
      previous: prev,
      current: curr,
      mutationSeverity: classifyMutationSeverity(field),
    });
  }

  return lines;
}

export function classifyMutationSeverity(field: SnapshotFieldKey): MutationSeverity {
  if (field === 'declaredStartDate') return 'blocking';
  if (field === 'employerPhoneVerified') return 'none';
  return 'advisory';
}

export function countSnapshotDiffLines(lines: SnapshotDiffLine[]): number {
  return lines.filter((line) => line.mutationSeverity !== 'none').length;
}
